import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import moment from 'moment';
import TranslationProvider from '../../providers/TranslationProvider';
import TicketQuantitySelector from '../TicketQuantitySelector';
import TicketSVG from '../../svg/icons/ic_ticket_20x20.svg';
import Info from '../../svg/icons/ic_info-outline_20x20.svg';
import Icon from '../Icon';
import Loader from '../Loader';

import EventTicketType from '../EventTicketType';
import NotifyButton from '../NotifyButton';
import eventTypes from '../../constants/eventTypes';
import GdaPromotional, { PROMOTION_CONTEXT } from '../GdaPromotional';
import { isInFuture, isInPast } from '../../utils/resyCoreUtils';
import formatDateAndTime from '../../utils/dateUtils';

import './EventBooking.scss';

const { SEATED } = eventTypes;
const GDA_EXCLUSIVE_ID = 1000;
const TIME_FORMAT = 'HH:mm:ss';
const LT_FORMAT = 'LT';

const renderGeneralSaleDate = (
  generalSaleDate,
  shouldShowNotifySubtext,
  isPreSaleOnForGdaUsers,
  publishDate,
  timezone
) => {
  if (
    generalSaleDate
    && isInFuture(generalSaleDate)
    && !isPreSaleOnForGdaUsers
  ) {
    const { formattedDay: fDay, formattedTime: fPublishTime } = formatDateAndTime(generalSaleDate, timezone);

    return (
      <div className="EventBooking__sale-messaging__general">
        <Icon component={TicketSVG} name="ticket" role="presentation" />
        <div className="EventBooking__sale-messaging__general__message">
          <TranslationProvider
            translationKey="event_on_sale_general"
            translationValues={{ date: fDay, time: fPublishTime }}
          >
            <span data-testid="publish-date">{`Event goes on sale on ${fDay} at ${fPublishTime}`}</span>
          </TranslationProvider>
          {shouldShowNotifySubtext && (
            <TranslationProvider
              className="notify_subtext"
              translationKey="set_a_notify_message"
            >
              Set a notify so you don’t miss out.
            </TranslationProvider>
          )}
        </div>
      </div>
    );
  }

  return null;
};

const checkForGdaPresale = (dates) => dates?.find(({ exclusive_id: id }) => id === GDA_EXCLUSIVE_ID);

const getPresaleDates = (dates) => {
  const gdaPresaleDates = checkForGdaPresale(dates);

  if (gdaPresaleDates?.pre_sale_date) {
    const [day, time] = gdaPresaleDates.pre_sale_date.split(' ');
    const fDay = moment(day).format('MMM D');
    const fTime = moment(time, TIME_FORMAT).format(LT_FORMAT);

    return {
      date: fDay,
      time: fTime,
    };
  }

  return null;
};

const canBookEvent = (event, userIsGda) => {
  let saleDate = event.generalSaleDate;

  if (event.isPresale && userIsGda) {
    const gdaPresaleDate = checkForGdaPresale(
      event.presaleDates
    )?.pre_sale_date;
    if (gdaPresaleDate) saleDate = gdaPresaleDate;
  }

  return isInPast(saleDate);
};

const EventBooking = (props) => {
  const {
    Analytics,
    context,
    event,
    onTicketQuantitySelect,
    previewMode,
    selectedQuantity,
    selectNotify,
    selectResy,
    showNotify,
    taxIncluded,
    ticketTypes,
    type,
    userIsGda,
    login,
    enableBookNow,
    eventLargePartyMessage,
    selectedCoreSeats,
  } = props;

  const {
    event: { generalSaleDate, ticketPackages = [], timezone },
  } = props;

  const [showLargePartyMessage, setShowLargePartyMessage] = useState(false);

  const isGeneralSaleInFuture = isInFuture(generalSaleDate);
  const publishDate = ticketPackages[0]?.publish_date;
  let isPublishDateInFuture = false;

  if (publishDate) {
    isPublishDateInFuture = isInFuture(publishDate, timezone);
  }

  const isPreSaleOnForGdaUsers = userIsGda
    && event.isPresale
    && checkForGdaPresale(event.presaleDates)?.pre_sale_date;
  const isEventNotOnSaleYet = (isPublishDateInFuture || isGeneralSaleInFuture) && !isPreSaleOnForGdaUsers;
  const showNotifyMessaging = showNotify && !event.saleHasEnded;
  const eventIsLive = !isEventNotOnSaleYet || canBookEvent(event, userIsGda);
  const showNotifySubtext = showNotifyMessaging && !event.hasNotify && !isEventNotOnSaleYet;
  const showGeneralSaleDate = event.isPresale || isEventNotOnSaleYet;
  const saleDateToShow = isPublishDateInFuture
    ? publishDate
    : event.generalSaleDate;

  useEffect(() => {
    const isLargePartySizeSelected = selectedCoreSeats > event.maxPartySize && eventIsLive;
    setShowLargePartyMessage(isLargePartySizeSelected);
  }, [selectedCoreSeats]);

  return (
    <div className="EventBooking">
      <div className="EventBooking__sale-messaging">
        {showGeneralSaleDate
          && renderGeneralSaleDate(
            saleDateToShow,
            showNotifySubtext,
            isPreSaleOnForGdaUsers,
            publishDate,
            timezone
          )}
        {!eventIsLive && showNotifyMessaging && (
          <NotifyButton
            onReservationSelect={selectNotify}
            serviceTypeId={event.serviceTypeId}
            active={event.hasNotify}
            disabled={previewMode}
          />
        )}
      </div>

      {!userIsGda
        && event.isPresale
        && context !== 'widget'
        && getPresaleDates(event?.presaleDates) && (
          <div>
            <GdaPromotional
              variant={PROMOTION_CONTEXT.event}
              login={login}
              eventPreSaleTiming={getPresaleDates(event.presaleDates)}
            />
          </div>
      )}
      {eventIsLive && (
        <div className="EventBooking__top">
          <TicketQuantitySelector
            type={type}
            minAvailable={event.minPartySize}
            maxAvailable={event.maxPartySize}
            eventName={event.title}
            onChange={onTicketQuantitySelect}
            selected={selectedQuantity}
            eventLargePartyMessage={eventLargePartyMessage}
          />
          {showNotify && !event.saleHasEnded && !isPublishDateInFuture && (
            <>
              <div className="EventBooking__top-notify">
                {!event.hasNotify && (
                  <span className="desktop-only">
                    <TranslationProvider translationKey="get_notified_if_tickets_become_available">
                      Get notified if tickets become available.
                    </TranslationProvider>
                  </span>
                )}
                <NotifyButton
                  onReservationSelect={selectNotify}
                  serviceTypeId={event.serviceTypeId}
                  active={event.hasNotify}
                  disabled={previewMode}
                />
              </div>
              {!event.hasNotify && (
                <span className="mobile-only">
                  <TranslationProvider translationKey="get_notified_if_tickets_become_available">
                    Get notified if tickets become available.
                  </TranslationProvider>
                </span>
              )}
            </>
          )}
        </div>
      )}

      {ticketTypes.length === 0 && (
        <Loader containerHeight="100px" />
      )}

      {!!ticketTypes.length
        && !showLargePartyMessage
        && ticketTypes.map((ticketType) => {
          const {
            availableQuantity,
            basePriceNumber,
            description,
            price,
            priceNumber,
            slots,
            soldOut,
            isPresaleSoldOut,
            title,
            isGda,
            mandatoryAddOns,
          } = ticketType;

          return (
            <EventTicketType
              Analytics={Analytics}
              availableQuantity={availableQuantity}
              basePriceNumber={basePriceNumber}
              description={description}
              event={event}
              isGda={isGda}
              isPresale={event.isPresale}
              isPresaleSoldOut={isPresaleSoldOut}
              maxPartySize={event.maxPartySize}
              generalSaleDate={saleDateToShow}
              publishDate={publishDate}
              key={title}
              price={price}
              priceNumber={priceNumber}
              selectResy={selectResy}
              selectedQuantity={selectedQuantity}
              soldOut={soldOut}
              slots={slots}
              taxIncluded={taxIncluded}
              title={title}
              type={type}
              userIsGda={userIsGda}
              enableBookNow={enableBookNow}
              isEventNotOnSaleYet={isEventNotOnSaleYet}
              isPreSaleOnForGdaUsers={isPreSaleOnForGdaUsers}
              mandatoryAddOns={mandatoryAddOns}
            />
          );
        })}

      {showLargePartyMessage && eventLargePartyMessage && (
        <div className="EventBooking__large-party-message">
          <Icon
            width="17.5px"
            height="17.5px"
            component={Info}
            className="info-icon"
            name="info"
            aria-hidden="true"
          />
          <div>{eventLargePartyMessage}</div>
        </div>
      )}
    </div>
  );
};

const eventBookingPropTypes = {
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  context: PropTypes.string.isRequired,
  event: PropTypes.shape({
    hasNotify: PropTypes.bool,
    minPartySize: PropTypes.number,
    maxPartySize: PropTypes.number,
    saleHasEnded: PropTypes.bool,
    serviceTypeId: PropTypes.number,
    isPresale: PropTypes.bool,
    isPresaleSoldOut: PropTypes.bool,
    generalSaleDate: PropTypes.string,
    timezone: PropTypes.string,
    title: PropTypes.string,
    presaleDates: PropTypes.arrayOf(
      PropTypes.shape({
        exclusive_id: PropTypes.number,
        pre_sale_date: PropTypes.string,
      })
    ),
    ticketPackages: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  onTicketQuantitySelect: PropTypes.func.isRequired,
  selectedCoreSeats: PropTypes.number.isRequired,
  enableBookNow: PropTypes.bool.isRequired,
  previewMode: PropTypes.bool,
  selectNotify: PropTypes.func.isRequired,
  selectResy: PropTypes.func.isRequired,
  selectedQuantity: PropTypes.number,
  showNotify: PropTypes.bool,
  taxIncluded: PropTypes.bool,
  ticketTypes: PropTypes.arrayOf(
    PropTypes.shape({
      availableQuantity: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]).isRequired,
      description: PropTypes.arrayOf(PropTypes.string),
      event: PropTypes.shape({
        saleHasEnded: PropTypes.bool,
      }),
      isGda: PropTypes.bool,
      isPresale: PropTypes.bool,
      isPresaleSoldOut: PropTypes.bool,
      price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      priceNumber: PropTypes.number,
      selectedQuantity: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      slots: PropTypes.arrayOf(
        PropTypes.shape({
          badge: PropTypes.string,
          colors: PropTypes.shape({
            background: PropTypes.string,
            font: PropTypes.string,
          }),
          serviceTypeId: PropTypes.number,
          time: PropTypes.string,
          token: PropTypes.string,
          type: PropTypes.string,
        })
      ),
      soldOut: PropTypes.bool,
      title: PropTypes.string.isRequired,
      type: PropTypes.string,
    })
  ),
  type: PropTypes.string,
  userIsGda: PropTypes.bool,
  login: PropTypes.func,
  eventLargePartyMessage: PropTypes.string,
};

EventBooking.propTypes = eventBookingPropTypes;

EventBooking.defaultProps = {
  event: {},
  previewMode: false,
  selectedQuantity: 1,
  showNotify: false,
  taxIncluded: false,
  ticketTypes: [],
  type: SEATED,
  userIsGda: false,
  login: null,
  eventLargePartyMessage: null,
};

const mapStateToProps = ({ resyCore: { selectedCoreSeats } }) => ({
  selectedCoreSeats,
});

export {
  eventBookingPropTypes,
  EventBooking as ComponentUnderTest,
  mapStateToProps,
  canBookEvent,
  isInFuture,
  renderGeneralSaleDate,
  checkForGdaPresale,
  getPresaleDates,
};
export default connect(mapStateToProps)(EventBooking);
